// Tiny music-theory helper for the dubstep layers. Scales are arrays of
// semitone offsets from the root. We convert to Hz on demand using equal
// temperament so everything stays driven by a single rootHz uniform.

export type ScaleId = "minor" | "dorian" | "phrygian" | "harmonicMinor";

export const SCALES: Record<ScaleId, number[]> = {
  // Natural minor: dark, classic dubstep / house. (W H W W H W W)
  minor:        [0, 2, 3, 5, 7, 8, 10],
  // Dorian: minor with a raised 6th -> minor-but-hopeful flavour.
  dorian:       [0, 2, 3, 5, 7, 9, 10],
  // Phrygian: minor with a flat 2nd -> Spanish / metal / very dark.
  phrygian:     [0, 1, 3, 5, 7, 8, 10],
  // Harmonic minor: raised 7th -> exotic, dramatic.
  harmonicMinor:[0, 2, 3, 5, 7, 8, 11],
};

export function pitchHz(rootHz: number, semitones: number): number {
  return rootHz * Math.pow(2, semitones / 12);
}

// Pick the n-th note of a scale. Wraps and octave-shifts so step >= 7
// just continues up. Used by ArpSynth for ascending patterns.
export function arpNoteHz(rootHz: number, scale: ScaleId, step: number): number {
  const semis = SCALES[scale];
  const n = semis.length;
  const oct = Math.floor(step / n);
  const idx = ((step % n) + n) % n;
  return pitchHz(rootHz, (semis[idx] ?? 0) + oct * 12);
}

// Return [root, third, fifth] of the chord built on the scale's root.
// The third / fifth come straight off the scale so they stay diatonic.
export function rootChordHz(rootHz: number, scale: ScaleId): [number, number, number] {
  const semis = SCALES[scale];
  return [
    pitchHz(rootHz, 0),
    pitchHz(rootHz, semis[2] ?? 3),
    pitchHz(rootHz, semis[4] ?? 7),
  ];
}

// ---------------------------------------------------------------------
// Chord builders. Each takes a chord-root frequency and the active scale
// and returns up to four diatonic voices in Hz. The Composer uses them
// to build ResolvedChord notes from a ChordSpec; PadSynth and ChordStabs
// can then play whatever voicing fits the section.
//
// "Diatonic" here means: every chord tone is picked off the supplied
// scale, never a chromatic alteration. That keeps every chord in the
// progression in-key even on non-standard scales (phrygian, harmonic
// minor) where standard triad math would clash.

// Pick the n-th degree of the scale relative to a chord root, supporting
// negative or out-of-range indices by wrapping with octave offsets.
function degreeSemis(scale: ScaleId, step: number): number {
  const semis = SCALES[scale];
  const n = semis.length;
  const idx = ((step % n) + n) % n;
  const oct = Math.floor(step / n);
  return (semis[idx] ?? 0) + oct * 12;
}

// Triad: root + diatonic 3rd + diatonic 5th.
export function triadHz(rootHz: number, scale: ScaleId): [number, number, number] {
  return [
    rootHz,
    pitchHz(rootHz, degreeSemis(scale, 2)),
    pitchHz(rootHz, degreeSemis(scale, 4)),
  ];
}

// Sus2: root + diatonic 2nd + diatonic 5th. Open / floating, the
// signature future-bass voicing.
export function sus2Hz(rootHz: number, scale: ScaleId): [number, number, number] {
  return [
    rootHz,
    pitchHz(rootHz, degreeSemis(scale, 1)),
    pitchHz(rootHz, degreeSemis(scale, 4)),
  ];
}

// Sus4: root + diatonic 4th + diatonic 5th. Tense, resolves nicely.
export function sus4Hz(rootHz: number, scale: ScaleId): [number, number, number] {
  return [
    rootHz,
    pitchHz(rootHz, degreeSemis(scale, 3)),
    pitchHz(rootHz, degreeSemis(scale, 4)),
  ];
}

// Add9: root + 3rd + 5th + 9th (the 2nd up an octave). Lush, dreamy.
export function add9Hz(
  rootHz: number,
  scale: ScaleId
): [number, number, number, number] {
  return [
    rootHz,
    pitchHz(rootHz, degreeSemis(scale, 2)),
    pitchHz(rootHz, degreeSemis(scale, 4)),
    pitchHz(rootHz, degreeSemis(scale, 1) + 12),
  ];
}

// Seventh: root + 3rd + 5th + 7th. Diatonic 7th, so "minor 7" in minor
// scales and "major 7" in major. Smooth, jazzy.
export function seventhHz(
  rootHz: number,
  scale: ScaleId
): [number, number, number, number] {
  return [
    rootHz,
    pitchHz(rootHz, degreeSemis(scale, 2)),
    pitchHz(rootHz, degreeSemis(scale, 4)),
    pitchHz(rootHz, degreeSemis(scale, 6)),
  ];
}

// Power chord: root + 5th, no third. Punchy / neutral. Skips diatonic
// quality so it works under wobbling / heavy distortion.
export function powerHz(rootHz: number, scale: ScaleId): [number, number] {
  return [rootHz, pitchHz(rootHz, degreeSemis(scale, 4))];
}
